import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Search, Send, Heart, MessageSquare, Trash2, Folder, FolderTree, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Community — cross-agent message board.
 *
 * Layout: category tree on the left, post list + detail on the right.
 * On mobile the sidebar becomes a horizontal chip strip at the top.
 * All state lives in this file — the feature is contained enough that
 * splitting it across files would obscure the flow more than it'd help.
 */
export default function Community() {
  const { agent } = useAuth();
  const [cats, setCats] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [posts, setPosts] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [openPost, setOpenPost] = useState(null); // full post w/ comments
  const [showNewPost, setShowNewPost] = useState(false);
  const [showAddCat, setShowAddCat] = useState(false);

  const loadCats = useCallback(async () => {
    const { data } = await api.get("/community/categories");
    setCats(data);
    setActiveCat(prev => prev || data.find(c => !c.parent_id) || data[0] || null);
  }, []);
  const loadPosts = useCallback(async () => {
    if (!activeCat) return;
    setLoading(true);
    const url = `/community/posts?category_id=${activeCat.id}${q ? `&q=${encodeURIComponent(q)}` : ""}`;
    const { data } = await api.get(url);
    setPosts(data); setLoading(false);
  }, [activeCat, q]);

  useEffect(() => { loadCats(); }, [loadCats]);
  useEffect(() => { loadPosts(); }, [loadPosts]);

  // Build the tree once per cats change.
  const tree = useMemo(() => {
    const roots = cats.filter(c => !c.parent_id);
    return roots.map(r => ({ ...r, children: cats.filter(c => c.parent_id === r.id) }));
  }, [cats]);

  const toggleLike = async (post) => {
    const { data } = await api.post(`/community/posts/${post.id}/like`);
    setPosts(prev => prev.map(p => p.id === post.id ? { ...p, liked: data.liked, likes_count: data.likes_count } : p));
    if (openPost?.id === post.id) setOpenPost(o => ({ ...o, liked: data.liked, likes_count: data.likes_count }));
  };

  return (
    <div className="max-w-6xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter text-slate-900">Community</h1>
          <p className="mt-1 text-sm text-slate-500">A private lounge for agents. Ask questions, swap referrals, share strategies.</p>
        </div>
        <button
          type="button"
          onClick={() => setShowNewPost(true)}
          disabled={!activeCat}
          className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:opacity-50"
          style={{ background: "var(--primary-hex)" }}
          data-testid="community-new-post-btn"
        >
          <Plus className="h-4 w-4" /> New post
        </button>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        {/* Categories sidebar */}
        <aside className="rounded-lg border border-slate-200 bg-white p-3" data-testid="community-cats">
          <div className="flex items-center justify-between px-2 pb-2">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Topics</div>
            <button
              type="button"
              onClick={() => setShowAddCat(true)}
              className="text-slate-500 hover:text-slate-900 p-1"
              title="Add category"
              data-testid="community-add-cat-btn"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
          <div className="space-y-0.5">
            {tree.map(root => (
              <div key={root.id}>
                <CatButton c={root} active={activeCat?.id === root.id} onClick={() => setActiveCat(root)} depth={0} />
                {root.children.length > 0 && (
                  <div className="ml-3 border-l border-slate-200 pl-2 mt-0.5 space-y-0.5">
                    {root.children.map(sub => (
                      <CatButton key={sub.id} c={sub} active={activeCat?.id === sub.id} onClick={() => setActiveCat(sub)} depth={1} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </aside>

        {/* Feed */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder={`Search ${activeCat?.name || "posts"}…`}
                className="w-full rounded-md border border-slate-300 pl-9 pr-3 py-2 text-sm"
                data-testid="community-search"
              />
            </div>
            <div className="text-xs text-slate-500 hidden sm:block">
              {posts.length} post{posts.length === 1 ? "" : "s"}
            </div>
          </div>

          {loading ? (
            <div className="text-sm text-slate-500 p-8 text-center">Loading…</div>
          ) : posts.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm text-slate-500" data-testid="community-empty">
              <MessageSquare className="h-8 w-8 mx-auto text-slate-300 mb-2" />
              Nothing here yet. Be the first to post in <strong>{activeCat?.name}</strong>.
            </div>
          ) : (
            <div className="space-y-3">
              {posts.map(p => (
                <PostCard key={p.id} post={p} currentAgentId={agent?.id} onLike={() => toggleLike(p)} onOpen={() => setOpenPost({ ...p, comments: [] })} onDelete={async () => {
                  if (!confirm("Delete this post?")) return;
                  await api.delete(`/community/posts/${p.id}`);
                  setPosts(prev => prev.filter(x => x.id !== p.id));
                }} />
              ))}
            </div>
          )}
        </div>
      </div>

      {showNewPost && (
        <NewPostDialog cat={activeCat} cats={cats}
          onClose={() => setShowNewPost(false)}
          onCreated={p => { setShowNewPost(false); setPosts(prev => [p, ...prev]); toast.success("Posted"); }}
        />
      )}
      {showAddCat && (
        <AddCatDialog cats={cats}
          onClose={() => setShowAddCat(false)}
          onCreated={c => { setShowAddCat(false); setCats(prev => [...prev, c]); toast.success("Added"); }}
        />
      )}
      {openPost && (
        <PostDialog postId={openPost.id} currentAgentId={agent?.id}
          onClose={() => setOpenPost(null)}
          onLike={() => toggleLike(openPost)}
          onDeleted={() => { setOpenPost(null); setPosts(prev => prev.filter(x => x.id !== openPost.id)); }}
        />
      )}
    </div>
  );
}

function CatButton({ c, active, onClick, depth }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left ${active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}
      data-testid={`community-cat-${c.slug}`}
    >
      {depth === 0 ? <FolderTree className="h-3.5 w-3.5 opacity-70" /> : <ChevronRight className="h-3 w-3 opacity-60" />}
      <span className="flex-1 truncate">{c.name}</span>
      {c.post_count > 0 && (
        <span className={`text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded ${active ? "bg-white/20" : "bg-slate-200 text-slate-600"}`}>{c.post_count}</span>
      )}
    </button>
  );
}

function AvatarInitials({ agent, size = 32 }) {
  const initials = (agent?.full_name || agent?.email || "?").slice(0, 2).toUpperCase();
  const s = `${size}px`;
  if (agent?.photo_url) {
    return <img src={agent.photo_url} alt="" className="rounded-full object-cover ring-1 ring-slate-200 shrink-0" style={{ width: s, height: s }} />;
  }
  return (
    <div className="rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-600 text-xs shrink-0" style={{ width: s, height: s }}>
      {initials}
    </div>
  );
}

function PostCard({ post, currentAgentId, onLike, onOpen, onDelete }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 hover:border-slate-300 transition-colors" data-testid={`community-post-${post.id}`}>
      <div className="flex items-start gap-3">
        <AvatarInitials agent={post.author} size={36} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="font-semibold text-slate-900 truncate">{post.author.full_name || "Agent"}</span>
            <span className="text-xs text-slate-400">· {new Date(post.created_at).toLocaleDateString()}</span>
          </div>
          <button type="button" onClick={onOpen} className="mt-1 block text-left">
            <div className="font-heading text-lg font-bold tracking-tight text-slate-900 hover:text-slate-700">{post.title}</div>
            <p className="mt-1 text-sm text-slate-600 line-clamp-2">{post.body}</p>
          </button>
          <div className="mt-3 flex items-center gap-4 text-xs">
            <button type="button" onClick={onLike} className={`inline-flex items-center gap-1.5 font-semibold ${post.liked ? "text-rose-600" : "text-slate-500 hover:text-slate-900"}`} data-testid={`community-like-${post.id}`}>
              <Heart className="h-3.5 w-3.5" fill={post.liked ? "currentColor" : "none"} />
              {post.likes_count}
            </button>
            <button type="button" onClick={onOpen} className="inline-flex items-center gap-1.5 font-semibold text-slate-500 hover:text-slate-900">
              <MessageSquare className="h-3.5 w-3.5" /> {post.comments_count}
            </button>
            {post.author.id === currentAgentId && (
              <button type="button" onClick={onDelete} className="ml-auto text-slate-400 hover:text-rose-600" title="Delete">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ModalShell({ title, onClose, children, footer }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4 py-8 overflow-y-auto">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <h3 className="font-heading text-xl font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5">{children}</div>
        {footer && <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100 bg-slate-50 rounded-b-xl">{footer}</div>}
      </div>
    </div>
  );
}

function NewPostDialog({ cat, cats, onClose, onCreated }) {
  const [form, setForm] = useState({ category_id: cat?.id || "", title: "", body: "" });
  const [busy, setBusy] = useState(false);

  // Precompute the category groups once per cats change instead of
  // re-filtering the whole list on every keystroke of title/body.
  const catGroups = useMemo(() => {
    const roots = cats.filter(c => !c.parent_id);
    const hasSubs = cats.some(c => c.parent_id);
    if (!hasSubs) return { flat: cats, groups: [] };
    return {
      flat: [],
      groups: roots.map(root => ({
        root,
        children: cats.filter(c => c.parent_id === root.id),
      })),
    };
  }, [cats]);

  const submit = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title and body are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/community/posts", form);
      onCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Post failed"); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="New post" onClose={onClose} footer={
      <>
        <button onClick={onClose} className="rounded-md border border-slate-300 px-4 py-2 text-sm">Cancel</button>
        <button onClick={submit} disabled={busy} className="rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: "var(--primary-hex)" }} data-testid="community-post-submit">
          <Send className="inline h-4 w-4 mr-1" /> {busy ? "Posting…" : "Post"}
        </button>
      </>
    }>
      <div className="space-y-4">
        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Category</label>
          <select value={form.category_id} onChange={e => setForm({ ...form, category_id: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="community-post-cat">
            {catGroups.flat.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
            {catGroups.groups.map(({ root, children }) => (
              <optgroup key={root.id} label={root.name}>
                <option value={root.id}>{root.name}</option>
                {children.map(sub => <option key={sub.id} value={sub.id}>— {sub.name}</option>)}
              </optgroup>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Title</label>
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="community-post-title" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Body</label>
          <textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} rows={6} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="community-post-body" />
        </div>
      </div>
    </ModalShell>
  );
}

function AddCatDialog({ cats, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState("");
  const [busy, setBusy] = useState(false);

  // Precompute the parent-eligible list once per cats change so the
  // dropdown doesn't refilter on every character typed into the name field.
  const parentOptions = useMemo(() => cats.filter(c => !c.parent_id), [cats]);

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/community/categories", { name: name.trim(), parent_id: parentId || null });
      onCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };
  return (
    <ModalShell title="Add category" onClose={onClose} footer={
      <>
        <button onClick={onClose} className="rounded-md border border-slate-300 px-4 py-2 text-sm">Cancel</button>
        <button onClick={submit} disabled={busy || !name.trim()} className="rounded-md px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: "var(--primary-hex)" }} data-testid="community-add-cat-submit">
          {busy ? "Adding…" : "Add"}
        </button>
      </>
    }>
      <div className="space-y-4">
        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Name</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Title Companies" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="community-cat-name" autoFocus />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Parent <span className="text-slate-400">· optional</span></label>
          <select value={parentId} onChange={e => setParentId(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
            <option value="">— Top-level category —</option>
            {parentOptions.map(c => <option key={c.id} value={c.id}>Under: {c.name}</option>)}
          </select>
        </div>
      </div>
    </ModalShell>
  );
}

function PostDialog({ postId, currentAgentId, onClose, onLike, onDeleted }) {
  const [post, setPost] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/community/posts/${postId}`).then(r => setPost(r.data));
  }, [postId]);
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/community/posts/${postId}/comments`, { body: text.trim() });
      setPost(p => ({ ...p, comments: [...p.comments, data], comments_count: (p.comments_count || 0) + 1 }));
      setText("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Comment failed"); }
    finally { setBusy(false); }
  };
  if (!post) return <ModalShell title="Loading…" onClose={onClose}>Loading…</ModalShell>;
  return (
    <ModalShell title={post.title} onClose={onClose}>
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <AvatarInitials agent={post.author} size={36} />
          <div>
            <div className="font-semibold text-slate-900 text-sm">{post.author.full_name}</div>
            <div className="text-xs text-slate-400">{new Date(post.created_at).toLocaleString()}</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button type="button" onClick={onLike} className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded ${post.liked ? "bg-rose-50 text-rose-600" : "bg-slate-100 text-slate-600"}`}>
              <Heart className="h-3.5 w-3.5" fill={post.liked ? "currentColor" : "none"} /> {post.likes_count}
            </button>
            {post.author.id === currentAgentId && (
              <button type="button" onClick={async () => { if (confirm("Delete post?")) { await api.delete(`/community/posts/${post.id}`); onDeleted(); } }} className="text-slate-400 hover:text-rose-600">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        <p className="whitespace-pre-wrap text-sm text-slate-800 leading-relaxed">{post.body}</p>
        <div className="border-t border-slate-100 pt-4">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Comments · {post.comments.length}</div>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {post.comments.map(c => (
              <div key={c.id} className="flex items-start gap-2">
                <AvatarInitials agent={c.author} size={28} />
                <div className="flex-1">
                  <div className="text-xs font-semibold text-slate-900">{c.author.full_name} <span className="font-normal text-slate-400">· {new Date(c.created_at).toLocaleDateString()}</span></div>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{c.body}</p>
                </div>
              </div>
            ))}
            {post.comments.length === 0 && <div className="text-xs text-slate-400 italic">No comments yet — start the conversation.</div>}
          </div>
          <div className="mt-4 flex items-start gap-2">
            <textarea value={text} onChange={e => setText(e.target.value)} rows={2} placeholder="Write a comment…" className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm" data-testid="community-comment-input" />
            <button type="button" onClick={submit} disabled={busy || !text.trim()} className="rounded-md px-3 py-2 text-sm font-semibold text-white disabled:opacity-50" style={{ background: "var(--primary-hex)" }} data-testid="community-comment-submit">
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}
